/* Proprietary - see LICENSE */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Login.css';
import apiClient from '../config/apiClient';
import MfaSetup from './MfaSetup';
import MfaVerify from './MfaVerify';

const Login = ({ setAuth }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaState, setMfaState] = useState(null); // null | { status, mfaToken }
  // A starting password lets the user in only far enough to pick their own.
  const [pwChange, setPwChange] = useState(null); // null | { changeToken }
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    await loginWith(email, password);
  };

  const loginWith = async (loginEmail, loginPassword) => {
    setError('');
    setLoading(true);

    try {
      const body = { email: loginEmail, password: loginPassword };
      // Include remember token if available
      const rememberToken = localStorage.getItem('mfaRememberToken');
      if (rememberToken) {
        body.mfaRememberToken = rememberToken;
      }

      const response = await apiClient.post('/auth/login', body);

      if (response.data.status === 'password_change_required') {
        setPwChange({ changeToken: response.data.changeToken });
        return;
      }
      // Handle MFA states
      if (response.data.status === 'mfa_setup_required') {
        setMfaState({ status: 'mfa_setup_required', mfaToken: response.data.mfaToken });
        return;
      }
      if (response.data.status === 'mfa_required') {
        setMfaState({ status: 'mfa_required', mfaToken: response.data.mfaToken });
        return;
      }

      // Normal login (no MFA) — shouldn't happen once MFA is enforced
      completeLogin(response.data);
    } catch (err) {
      // If remember token was invalid, clear it
      if (err.response?.status === 401) {
        localStorage.removeItem('mfaRememberToken');
      }
      setError(err.response?.data?.message || t('login.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = (data) => {
    sessionStorage.setItem('token', data.access_token);
    if (data.refresh_token) {
      sessionStorage.setItem('refreshToken', data.refresh_token);
    }
    sessionStorage.setItem('user', JSON.stringify(data.user));

    const role = data?.user?.role;
    const isMultiTenant = data?.user?.multiTenant === true;
    const shouldSelectTenant = ['super_admin', 'owner', 'provider_admin'].includes(role) || isMultiTenant;
    if (shouldSelectTenant) {
      sessionStorage.removeItem('selectedTenantId');
    } else if (data?.user?.tenantId) {
      sessionStorage.setItem('selectedTenantId', data.user.tenantId);
    } else {
      sessionStorage.removeItem('selectedTenantId');
    }

    setAuth(true);
  };

  const handleMfaComplete = () => {
    setAuth(true);
  };

  const handleFirstPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError(t('login.passwordMismatch'));
      return;
    }
    const rules = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/;
    if (newPassword.length < 8 || !rules.test(newPassword)) {
      setError(t('login.passwordRules'));
      return;
    }
    setLoading(true);
    try {
      await apiClient.post('/auth/first-password', { changeToken: pwChange.changeToken, password: newPassword });
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || t('login.firstPasswordFailed'));
      return;
    }
    setLoading(false);
    setPwChange(null);
    setPassword(newPassword);
    setNewPassword('');
    setConfirmPassword('');
    // Continue as a normal login with the new password: MFA setup comes next.
    await loginWith(email, newPassword);
  };

  if (pwChange) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img className="login-logo" src="/images/inco-logo4.png" alt="INCO" />
            <h2>{t('login.firstPasswordTitle')}</h2>
            <p>{t('login.firstPasswordIntro')}</p>
          </div>
          <form onSubmit={handleFirstPassword} className="login-form">
            {error && <div className="error-message">{error}</div>}
            <div className="form-group">
              <label htmlFor="new-password">{t('login.newPassword')}</label>
              <input
                type="password"
                id="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoFocus
                autoComplete="new-password"
              />
              <small>{t('login.passwordRules')}</small>
            </div>
            <div className="form-group">
              <label htmlFor="confirm-password">{t('login.confirmPassword')}</label>
              <input
                type="password"
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? t('login.settingPassword') : t('login.setPassword')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Show MFA Setup wizard
  if (mfaState?.status === 'mfa_setup_required') {
    return (
      <MfaSetup
        mfaToken={mfaState.mfaToken}
        onComplete={handleMfaComplete}
        onCancel={() => setMfaState(null)}
      />
    );
  }

  // Show MFA Verify screen
  if (mfaState?.status === 'mfa_required') {
    return (
      <MfaVerify
        mfaToken={mfaState.mfaToken}
        onComplete={handleMfaComplete}
      />
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img className="login-logo" src="/images/inco-logo4.png" alt="INCO" />
          <p>{t('login.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="email">{t('login.email')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="email@company.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('login.password')}</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
            />
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? t('login.signingIn') : t('login.signIn')}
          </button>

          <div className="forgot-password-link">
            <Link to="/forgot-password">{t('login.forgotPassword', 'Forgot your password?')}</Link>
          </div>
        </form>


      </div>
    </div>
  );
};

export default Login;
